import React from 'react';

import { createColumnHelper } from '@tanstack/react-table';
import { useIntl } from 'react-intl';
import { useSearchParams } from 'react-router-dom';

import SettingsContext from 'src/features/administration/context/SettingsContext';
import { PERMISSION } from 'src/features/auth';
import CellLinkWithIcon from 'src/features/membership/components/cell/CellLinkWIthIcon';
import { PATH } from 'src/features/navigation/utils/constants';
import { PaymentsListDTO, useListPayments } from 'src/features/payment/api/useListPayments';
import PaymentSidePanel from 'src/features/payment/components/PaymentSidePanel';
import {
    PAYMENTS_SIDE_PANEL,
    paymentServiceTypeTranslationMap,
    paymentSourceTranslationMap,
    paymentTypeBadgeOptions,
} from 'src/features/payment/utils/constants';
import { paymentsMessages } from 'src/features/payment/utils/messages';
import { Payment, PAYMENT_ITEM_TYPE } from 'src/features/payment/utils/types';
import { CellSidePanelActions } from 'src/features/visitor/components/cell/CellSidePanelActions';
import { visitorMessages } from 'src/features/visitor/utils/messages';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { useParking } from 'src/providers/ParkingProvider';
import SidePanel from 'src/registry/components/sidePanel/SidePanel';
import {
    CellBadge,
    CellDatetime,
    DataTable,
    DataTableControls,
    DataTablePagination,
} from 'src/registry/components/table';
import DataTableColumnHeader from 'src/registry/components/table/DataTableColumnHeader';
import { INPUT_TYPE } from 'src/registry/components/table/utils/constants';
import useFilters from 'src/registry/hooks/useFilters';
import usePagination from 'src/registry/hooks/usePagination';
import useSearch from 'src/registry/hooks/useSearch';
import { useSingleRowSelection } from 'src/registry/hooks/useSingleRowSelection';
import useSorting from 'src/registry/hooks/useSorting';
import { useSidePanel } from 'src/registry/providers/SidePanelProvider';
import { commonMessages } from 'src/utils/messages';

type PaymentsTableProps = {
    itemId?: string;
    itemType?: PAYMENT_ITEM_TYPE;
    visitorId?: string;
    entityId?: string;
    hasExtraColumns?: boolean;
    enableSearchParams?: boolean;
};

const PaymentsTable = ({
    itemId,
    visitorId,
    entityId,
    hasExtraColumns = false,
    itemType,
    enableSearchParams = false,
}: PaymentsTableProps) => {
    const { formatMessage, formatNumber } = useIntl();
    const { querySortingOptions, ...sorting } = useSorting({ initialSortingState: [{ id: 'date', desc: true }] });
    const { querySearchOptions, ...search } = useSearch({ globalFilterKey: 'search' });
    const { queryFiltersOptions, ...filters } = useFilters();
    const { queryPaginationOptions, ...pagination } = usePagination();
    const { parkingId } = useParking();
    const [searchParams] = useSearchParams();
    const initialSearch = searchParams.get('search');
    const { openPanel } = useSidePanel();
    const settingsData = React.useContext(SettingsContext);

    const paymentsQuery: PaymentsListDTO = {
        parkingId,
    };
    if (itemId) {
        if (itemType === PAYMENT_ITEM_TYPE.ACTIVE_STAY) {
            paymentsQuery.relation_type = PAYMENT_ITEM_TYPE.ACTIVE_STAY;
            paymentsQuery.relation_value = itemId;
        }
        if (itemType === PAYMENT_ITEM_TYPE.PAST_STAY) {
            paymentsQuery.relation_type = PAYMENT_ITEM_TYPE.PAST_STAY;
            paymentsQuery.relation_value = itemId;
        }
    } else if (entityId) {
        if (itemType === PAYMENT_ITEM_TYPE.MEMBERSHIP) {
            paymentsQuery.relation_type = PAYMENT_ITEM_TYPE.MEMBERSHIP;
            paymentsQuery.relation_value = entityId;
        }
        if (itemType !== PAYMENT_ITEM_TYPE.MEMBERSHIP) {
            paymentsQuery.relation_type = 'entity';
            paymentsQuery.relation_value = entityId;
        }
    } else if (visitorId) {
        paymentsQuery.relation_type = 'visitor';
        paymentsQuery.relation_value = visitorId;
    }

    const {
        data: listPayments,
        isFetchedAfterMount,
        isLoading,
        isError,
    } = useListPayments({
        ...paymentsQuery,
        ...querySearchOptions,
        ...querySortingOptions,
        ...queryFiltersOptions,
        ...queryPaginationOptions,
    });

    const { rowSelectionState, clearSelection, setRowSelectionState } = useSingleRowSelection();

    const singleRowSelection = React.useCallback(
        (rowId: string) => {
            setRowSelectionState({ [rowId]: true });
        },
        [setRowSelectionState],
    );

    React.useEffect(() => {
        if (initialSearch) search.setGlobalFilterState(initialSearch);
    }, []);

    React.useEffect(() => {
        if (initialSearch && listPayments && listPayments.count === 1 && !isFetchedAfterMount) {
            singleRowSelection(listPayments?.results[0].id);
            openPanel(PAYMENTS_SIDE_PANEL);
        }
    }, [isFetchedAfterMount, initialSearch, listPayments, openPanel, singleRowSelection]);

    const accessActiveStays = useHasPermissions([PERMISSION.ACCESS_ACTIVE_STAYS]);
    const accessPastStays = useHasPermissions([PERMISSION.ACCESS_PAST_STAYS]);
    const accessVisitors = useHasPermissions([PERMISSION.ACCESS_VISITORS]);
    const accessMemberships = useHasPermissions([PERMISSION.ACCESS_MEMBERSHIPS]);
    const accessOperations = useHasPermissions([PERMISSION.ACCESS_PAY_STATION_OPERATIONS]);

    const paymentId = React.useMemo(() => {
        const firstSelected = Object.keys(rowSelectionState)[0];
        if (!listPayments || !firstSelected) return '';
        return listPayments.results.find((row) => row.id === firstSelected)?.id;
    }, [listPayments, rowSelectionState]);

    const [defaultDateFilter] = React.useState(() => {
        const now = Date.now();

        return {
            afterDate: new Date(now - 30 * 24 * 60 * 60 * 1000),
            beforeDate: new Date(),
        };
    });

    const columnHelper = createColumnHelper<Payment>();
    const columns = [
        columnHelper.accessor('date', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableSorting: true,
            enableColumnFilter: true,
            cell: CellDatetime,
            meta: {
                title: formatMessage(commonMessages.date),
                inputType: INPUT_TYPE.DATE,
                dateFilter: defaultDateFilter,
            },
        }),
        columnHelper.accessor('amount', {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: ({ row }) => {
                return formatNumber(row.original.amount / 100, {
                    style: 'currency',
                    currency: row.original.currency ?? settingsData.default_currency,
                });
            },
            meta: {
                title: formatMessage(paymentsMessages.amount),
            },
        }),
        columnHelper.accessor('payment_payload_details.service_type', {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: ({ row }) => {
                const serviceType = row.original?.payment_payload_details?.service_type;
                const hasServiceTypeTranslation = Object.hasOwn(paymentServiceTypeTranslationMap, serviceType);

                return hasServiceTypeTranslation
                    ? formatMessage(paymentServiceTypeTranslationMap[serviceType])
                    : serviceType;
            },
            meta: {
                title: formatMessage(paymentsMessages.serviceType),
            },
        }),
        ...(hasExtraColumns
            ? [
                  columnHelper.accessor('payment_payload_details.item_display', {
                      header: DataTableColumnHeader,
                      enableHiding: true,
                      enableColumnFilter: true,
                      cell: ({ row }) => {
                          const serviceId = row.original.payment_payload_details.item_id;
                          const service = row.original.payment_payload_details.item_display;
                          const stayType: PAYMENT_ITEM_TYPE = row.original.payment_payload_details.item_type;
                          const membershipCategory = row.original.payment_payload_details.membership_category;

                          const urlLink =
                              stayType === PAYMENT_ITEM_TYPE.ACTIVE_STAY
                                  ? `/${PATH.VISITORS}/active-stay/dashboard/${serviceId}`
                                  : stayType === PAYMENT_ITEM_TYPE.PAST_STAY
                                    ? `/${PATH.VISITORS}/past-stay/dashboard/${serviceId}`
                                    : `/${PATH.MEMBERSHIPS}/${membershipCategory}/${serviceId}`;

                          const hasPermissions =
                              stayType === PAYMENT_ITEM_TYPE.ACTIVE_STAY
                                  ? accessActiveStays
                                  : stayType === PAYMENT_ITEM_TYPE.PAST_STAY
                                    ? accessPastStays
                                    : accessMemberships;

                          return serviceId && urlLink ? (
                              <CellLinkWithIcon
                                  toPath={urlLink}
                                  linkTitle={`#${service}`}
                                  hasPermissions={hasPermissions}
                              />
                          ) : (
                              '-'
                          );
                      },
                      meta: {
                          title: formatMessage(paymentsMessages.serviceColumnTitle),
                          options: [
                              {
                                  value: PAYMENT_ITEM_TYPE.MEMBERSHIP,
                                  label: formatMessage(visitorMessages.subscription),
                              },
                              { value: PAYMENT_ITEM_TYPE.ACTIVE_STAY, label: formatMessage(paymentsMessages.stay) },
                          ],
                      },
                      id: 'relation_type',
                  }),
                  columnHelper.accessor('payment_payload_details.visitor_display', {
                      header: DataTableColumnHeader,
                      enableHiding: true,
                      cell: ({ row }) => {
                          const visitorId = row.original.payment_payload_details.visitor_id;
                          const visitor = row.original.payment_payload_details.visitor_display;

                          return visitorId ? (
                              <CellLinkWithIcon
                                  toPath={`/${PATH.VISITORS}/visitor/${visitorId}`}
                                  linkTitle={visitor}
                                  hasPermissions={accessVisitors}
                              />
                          ) : (
                              '-'
                          );
                      },
                      meta: {
                          title: formatMessage(paymentsMessages.visitor),
                      },
                  }),
              ]
            : []),
        columnHelper.accessor('type', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableSorting: true,
            enableColumnFilter: true,
            cell: CellBadge,
            meta: {
                inputType: INPUT_TYPE.MULTI_SELECT,
                title: formatMessage(paymentsMessages.paymentType),
                badgeOptions: paymentTypeBadgeOptions,
            },
        }),
        columnHelper.accessor('payment_payload_details.receipt_number', {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: ({ row }) => {
                const receiptNumber = row.original.payment_payload_details?.receipt_number;
                return <span className="font-medium">{receiptNumber ? `#${receiptNumber}` : '-'}</span>;
            },
            meta: {
                title: formatMessage(paymentsMessages.receiptNumber),
            },
        }),
        columnHelper.accessor('payment_payload_details.payment_source_display', {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: ({ row }) => {
                const paymentSource = row.original.payment_payload_details?.payment_source_display;
                const paymentSourceType = row.original.payment_payload_details?.payment_source_type;
                return (
                    <div className="flex flex-col">
                        <span className="font-medium">{paymentSource}</span>
                        {paymentSourceType && (
                            <span className="whitespace-nowrap">{`${formatMessage(paymentSourceTranslationMap[paymentSourceType])}`}</span>
                        )}
                    </div>
                );
            },
            meta: {
                title: formatMessage(paymentsMessages.paidOn),
            },
        }),
        columnHelper.accessor('payment_payload_details.pay_station_operation_id', {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: ({ row }) => {
                const payStationOperationId = row.original.payment_payload_details?.pay_station_operation_id;
                const payStationOperationRef = row.original.payment_payload_details?.pay_station_operation_ref;

                return (
                    <div className="flex flex-col">
                        {payStationOperationId && payStationOperationRef ? (
                            <CellLinkWithIcon
                                toPath={`/${PATH.PAYMENTS}/operation/${payStationOperationId}`}
                                linkTitle={payStationOperationRef}
                                hasPermissions={accessOperations}
                            />
                        ) : (
                            '-'
                        )}
                    </div>
                );
            },
            meta: {
                title: formatMessage(paymentsMessages.operation),
            },
        }),
        columnHelper.display({
            id: 'actions',
            cell: (props) => (
                <CellSidePanelActions
                    {...props}
                    sidePanelName={PAYMENTS_SIDE_PANEL}
                    singleRowSelection={singleRowSelection}
                />
            ),
        }),
    ];

    const searchPlaceholder = `${formatMessage(paymentsMessages.licensePlate)}, ${formatMessage(paymentsMessages.stayRef)}, ${formatMessage(paymentsMessages.membershipRef)}, ${formatMessage(paymentsMessages.searchReceiptNumber)}`;

    return (
        <>
            <DataTable
                isLoading={isLoading}
                isError={isError}
                columns={columns as any}
                data={listPayments}
                pagination={pagination}
                sorting={sorting}
                search={search}
                filters={filters}
                enableLocalFilters={false}
                enableSearchParams={enableSearchParams}
                semantics={{
                    title: formatMessage(paymentsMessages.payments),
                    entryName: formatMessage(paymentsMessages.payment).toLowerCase(),
                    entryNames: formatMessage(paymentsMessages.payments).toLowerCase(),
                    sidePanelName: PAYMENTS_SIDE_PANEL,
                    placeholder: searchPlaceholder,
                }}
                getRowId={(row) => row.id.toString()}
                components={{
                    controls: DataTableControls,
                    pagination: DataTablePagination,
                }}
            />
            <SidePanel onClose={clearSelection} name={PAYMENTS_SIDE_PANEL}>
                <PaymentSidePanel paymentId={paymentId} accessFromBillingModule={hasExtraColumns} />
            </SidePanel>
        </>
    );
};

export default PaymentsTable;
